import {
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, Alert, Button,
  TextField, Typography, IconButton, InputAdornment, LinearProgress, Box,
} from '@mui/material';
import { DialogClose } from '@/components/DialogClose';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useEffect, useState } from 'react';
import { useChangePassword } from '@/api/auth';
import { ApiError } from '@/lib/api-client';

/**
 * Change-password dialog opened from the topbar user menu.
 *
 * Flow:
 *   • User enters current + new + confirm
 *   • Client validates: new ≠ current · confirm matches · meets length policy
 *   • Server verifies current password and re-hashes the new one
 *   • On success show a success message for 1.5s, then close
 *
 * Strength indicator is intentionally simple — backend enforces the real rule.
 */
export function ChangePasswordDialog({
  open, onClose,
}: { open: boolean; onClose: () => void }) {
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrent(''); setNext(''); setConfirm('');
      setShow({ current: false, next: false, confirm: false });
      setError(null); setSuccess(false);
    }
  }, [open]);

  const strength = scorePassword(next);

  const onSubmit = async () => {
    setError(null);
    if (!current) { setError('Enter your current password.'); return; }
    if (!next || next.length < 8) {
      setError('New password must be at least 8 characters.'); return;
    }
    if (next === current) {
      setError('New password must differ from the current one.'); return;
    }
    if (next !== confirm) {
      setError('New password and confirmation do not match.'); return;
    }

    try {
      await change.mutateAsync({ current_password: current, new_password: next });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (e) {
      setError(e instanceof ApiError ? e.detailMessage : 'Failed to change password.');
    }
  };

  return (
    <Dialog open={open} onClose={() => !change.isPending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Change Password  <DialogClose onClose={onClose} /></DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Pick a unique password of 8+ characters. Avoid reusing one from another site.
          </Typography>

          <TextField
            label="Current password" size="small" fullWidth required
            type={show.current ? 'text' : 'password'}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            disabled={change.isPending || success}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() =>
                    setShow((s) => ({ ...s, current: !s.current }))}>
                    {show.current ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box>
            <TextField
              label="New password" size="small" fullWidth required
              type={show.next ? 'text' : 'password'}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              disabled={change.isPending || success}
              helperText={next.length > 0 && next.length < 8 ? `${8 - next.length} more characters needed` : ' '}
              error={next.length > 0 && next.length < 8}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() =>
                      setShow((s) => ({ ...s, next: !s.next }))}>
                      {show.next ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {next.length > 0 && (
              <Box sx={{ mt: -0.5, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={strength.score * 25}
                  color={strength.color}
                  sx={{ flex: 1, height: 4, borderRadius: 2 }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 60, textAlign: 'right' }}>
                  {strength.label}
                </Typography>
              </Box>
            )}
          </Box>

          <TextField
            label="Confirm new password" size="small" fullWidth required
            type={show.confirm ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            disabled={change.isPending || success}
            error={confirm.length > 0 && confirm !== next}
            helperText={confirm.length > 0 && confirm !== next ? "Doesn't match" : ' '}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() =>
                    setShow((s) => ({ ...s, confirm: !s.confirm }))}>
                    {show.confirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">Password updated.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={change.isPending}>Cancel</Button>
        <Button variant="contained"
          onClick={onSubmit}
          disabled={change.isPending || success}>
          {change.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Strength scoring ────────────────────────────────────────────────────────

function scorePassword(pw: string): { score: 0 | 1 | 2 | 3 | 4; label: string; color: 'error' | 'warning' | 'info' | 'success' } {
  if (pw.length === 0) return { score: 0, label: '', color: 'error' };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  const score = Math.min(4, s) as 0 | 1 | 2 | 3 | 4;
  const labels = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'] as const;
  const colors = ['error', 'error', 'warning', 'info', 'success'] as const;
  return { score, label: labels[score], color: colors[score] };
}
